import { useQuery } from "@tanstack/react-query";
import { getWidgetActionsApiClient } from "@shared/apis/sidekick/api-factories";
import {
  TerritoryWithPolygonResponse,
  WidgetsControllersSidekickTerritoryNameTypeEnum,
} from "@shared/apis/sidekick/generated";
import { extractQueryState } from "../helpers/extract-query-state";

const WIDGETS_GET_TERRITORY_SUGGESTIONS_QUERY_KEY =
  "widgets-get-territory-suggestions";
const CACHE_TIME_IN_MS = 1000 * 60 * 60; // 60 minutes

type Params = {
  name: string;
  nameType?: WidgetsControllersSidekickTerritoryNameTypeEnum;
  city?: string;
  stateAbbreviated?: string;
};

export const buildWidgetsGetTerritoryByAreaQueryKey = (
  params: Params | null,
) => {
  return [WIDGETS_GET_TERRITORY_SUGGESTIONS_QUERY_KEY, { params }];
};

export function useWidgetsGetTerritoryByArea(props: {
  params: Params | null;
  onSuccess?: (data: TerritoryWithPolygonResponse) => void;
}) {
  const { params, onSuccess } = props;
  const api = getWidgetActionsApiClient();

  const query = useQuery({
    queryKey: buildWidgetsGetTerritoryByAreaQueryKey(params),
    queryFn: async () => {
      if (!params) throw new Error("Invalid parameters for query");
      const { name, nameType, city, stateAbbreviated } = params;
      const response = await api.widgetsControllersSidekickTerritory({
        name: name!,
        nameType,
        city,
        stateAbbreviated,
        simplificationTolerance: 10,
      });
      onSuccess?.(response);
      return response;
    },
    enabled: !!params,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = (error as any)?.response?.status || 0;
      if (failureCount < 3 && status === 429) {
        return true;
      }
      return false;
    },
    gcTime: CACHE_TIME_IN_MS,
    staleTime: CACHE_TIME_IN_MS,
  });

  return extractQueryState(query);
}
